// src/utils/facturaGenerator.js
import { formatearFechaTicket } from './ticketGenerator.js';
import { obtenerDatosEmpresa } from './tenantConfig';

const ANCHO = 32;

export const formatearNumero = (numero, decimals = 2) => {
  if (numero === null || numero === undefined || isNaN(parseFloat(numero))) {
    return decimals === 0 ? '0' : '0,00';
  }
  const number = parseFloat(numero);
  if (decimals === 0) {
    return Math.round(number).toLocaleString('es-VE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  return number.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatearPrecio = (precio, moneda = 'BS', showSymbol = true) => {
  if (precio === null || precio === undefined || isNaN(parseFloat(precio))) {
    if (!showSymbol) return moneda === 'USD' ? '0,00' : '0';
    return moneda === 'USD' ? '$0,00' : 'Bs.0';
  }
  const precioNum = parseFloat(precio);
  if (moneda === 'BS') {
    const formateado = formatearNumero(precioNum, 0);
    return showSymbol ? `Bs.${formateado}` : formateado;
  }
  const formateado = formatearNumero(precioNum, 2);
  return showSymbol ? `$${formateado}` : formateado;
};

export const calcularTotalBolivares = (totalUSD, tasa) => {
  if (!tasa || (!tasa.precio_usd_bs && !tasa.valor)) return 0;
  const totalUsdNum = parseFloat(totalUSD) || 0;
  const tasaNum = parseFloat(tasa.precio_usd_bs || tasa.valor) || 0;
  return totalUsdNum * tasaNum;
};

const centro = (texto) => {
  const str = String(texto || '');
  if (str.length >= ANCHO) return str.substring(0, ANCHO);
  const espacios = Math.floor((ANCHO - str.length) / 2);
  return ' '.repeat(espacios) + str;
};

const linea = (char = '-') => char.repeat(ANCHO);
const lineaDoble = () => '='.repeat(ANCHO);

const truncar = (texto, maxLen = 26) => {
  if (!texto) return '';
  const str = String(texto).trim();
  if (str.length <= maxLen) return str;
  return str.substring(0, maxLen - 2) + '..';
};

export const generarNumeroFactura = (prefijo = 'ORD') => {
  const fecha = new Date();
  const año = fecha.getFullYear().toString().slice(-2);
  const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
  const dia = fecha.getDate().toString().padStart(2, '0');
  const aleatorio = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `${prefijo}-${año}${mes}${dia}-${aleatorio}`;
};

export const generarHTMLFactura = (venta, cliente, usuario, tasa, productos) => {
  const empresa = obtenerDatosEmpresa(); // 👈 config dinámica

  const fecha = formatearFechaTicket(new Date().toISOString());
  const numero = venta.numeroFactura || venta.numero_factura || generarNumeroFactura('FR');
  const subtotal = parseFloat(venta.subtotalUsd || venta.subtotal_usd || venta.subtotal) || 0;
  const descuento = parseFloat(venta.descuentoUsd || venta.descuentoUSD || venta.descuento || 0);
  const totalUSD = parseFloat(venta.totalUsd || venta.total || venta.totalConInteres || venta.total_usd || 0);
  const tipoVenta = (venta.tipoVenta || venta.tipo_venta || 'CONTADO').toUpperCase();
  const estado = (venta.estado || 'Completada').toUpperCase();
  const metodoPago = (venta.metodoPago || venta.metodo_pago || 'EFECTIVO').toUpperCase();
  const montoInicial = parseFloat(venta.montoInicial || 0);
  const numeroCuotas = venta.numeroCuotas || 0;
  const tasaValor = parseFloat(tasa?.precio_usd_bs || tasa?.valor || tasa?.tasaVenta || tasa?.tasa_bs_usd || venta.tasaBsUsd || venta.tasa_bs_usd) || 0;
  const totalBs = totalUSD * tasaValor || parseFloat(venta.totalBs || venta.total_bs) || 0;
  const vendedor = usuario ? (usuario.nombre || usuario.nombre_completo || 'SISTEMA').toUpperCase() : 'SISTEMA';
  const clienteNombre = cliente ? `${cliente.nombre || ''} ${cliente.apellido || ''}`.toUpperCase() : 'CONSUMIDOR FINAL';
  const clienteDoc = cliente?.documento || '';
  const clienteTel = cliente?.telefono || '';

  let maxGarantiaMeses = 0;
  let maxGarantiaDias = 0;
  if (productos && productos.length > 0) {
    productos.forEach(p => {
      const gMeses = parseInt(p.garantiaMeses || p.garantia_meses || 0);
      const gDias = parseInt(p.garantiaDias || p.garantia_dias || 0);
      if (gMeses > maxGarantiaMeses) maxGarantiaMeses = gMeses;
      if (gDias > maxGarantiaDias) maxGarantiaDias = gDias;
    });
  }
  let labelGarantia = 'GARANTIA 1 MES';
  if (maxGarantiaMeses > 0) {
    labelGarantia = `GARANTIA ${maxGarantiaMeses} ${maxGarantiaMeses === 1 ? 'MES' : 'MESES'}`;
  } else if (maxGarantiaDias > 0) {
    labelGarantia = `GARANTIA ${maxGarantiaDias} ${maxGarantiaDias === 1 ? 'DIA' : 'DIAS'}`;
  }

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>ORDEN DE ENTREGA - ${numero}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; background: #fff; color: #000; width: 58mm; margin: 0 auto; padding: 0; font-size: 10px; line-height: 1.1; font-weight: 700; text-transform: uppercase; text-align: center; }
        .ticket-wrapper { width: 50mm; padding: 0; margin: 0 auto; text-align: center; }
        .logo-img { width: 50mm; max-width: 50mm; margin: 0 auto; padding: 0; display: block; }
        .linea { text-align: center; margin: 0; padding: 0; line-height: 1.1; }
        .bold { font-weight: 900; }
        .separador { text-align: center; margin: 0; padding: 0; line-height: 1.1; }
        @media print { @page { size: 58mm auto; margin: 0mm; } body { margin: 0; padding: 0; } .no-print { display: none; } }
      </style>
    </head>
    <body>
      <div class="ticket-wrapper">
        <img src="${empresa.logoBase64}" alt="Logo" class="logo-img" />
        <div class="linea">${empresa.direccion}</div>
        <div class="linea">${empresa.ciudad}</div>
        <div class="linea">${empresa.telefono1} / ${empresa.whatsapp}</div>
        <div class="separador">----------------------------</div>
        <div class="linea bold">ORDEN DE ENTREGA</div>
        <div class="separador">----------------------------</div>
        <div class="linea bold">NRO: ${numero} - ${fecha}</div>
        <div class="linea">${tipoVenta === 'CREDITO' ? 'CREDITO' : 'CONTADO'}</div>
        <div class="separador">----------------------------</div>
        <div class="linea bold">CLIENTE: ${clienteNombre}</div>
        ${clienteDoc ? `<div class="linea">${clienteDoc} ${clienteTel ? `| ${clienteTel}` : ''}</div>` : ''}
        ${!clienteDoc && clienteTel ? `<div class="linea">${clienteTel}</div>` : ''}
        <div class="separador">----------------------------</div>
        <div class="linea bold">PRODUCTOS</div>
        ${productos && productos.length > 0 ? productos.map(producto => {
          const precioUnit = parseFloat(producto.precioUnitarioUsd || producto.precio || producto.precio_unitario_usd || 0);
          const cantidad = parseFloat(producto.cantidad) || 1;
          const totalProd = parseFloat(producto.subtotalUsd) || (precioUnit * cantidad);
          const nombreProd = producto.nombre || producto.nombreProducto || producto.productoNombre || 'PRODUCTO';
          const precioStr = formatearPrecio(precioUnit, 'USD', false);
          const totalStr = formatearPrecio(totalProd, 'USD', false);
          return `<div class="linea">${cantidad} ${nombreProd}</div><div class="linea">$${precioStr} C/U - TOTAL: $${totalStr}</div>`;
        }).join('') : '<div class="linea bold">NO HAY PRODUCTOS</div>'}
        <div class="separador">----------------------------</div>
        <div class="linea">SUBTOTAL: $${formatearPrecio(subtotal, 'USD', false)}</div>
        ${descuento > 0 ? `<div class="linea">DESCUENTO: -$${formatearPrecio(descuento, 'USD', false)}</div>` : ''}
        ${tipoVenta === 'CREDITO' ? `<div class="linea">INTERES: $${formatearPrecio(totalUSD - (subtotal - descuento), 'USD', false)}</div>` : ''}
        <div class="separador">----------------------------</div>
        <div class="linea bold">TOTAL: $${formatearPrecio(totalUSD, 'USD', false)}</div>
        ${tasaValor > 0 ? `<div class="linea">TOTAL Bs: ${formatearPrecio(totalBs, 'BS', true)}</div><div class="linea">TASA: ${formatearPrecio(tasaValor, 'BS', true)}</div>` : ''}
        ${tipoVenta === 'CREDITO' && montoInicial > 0 ? `
          <div class="separador">----------------------------</div>
          <div class="linea bold">CREDITO</div>
          <div class="linea">INICIAL: $${formatearPrecio(montoInicial, 'USD', false)}</div>
          <div class="linea">SALDO: $${formatearPrecio(totalUSD - montoInicial, 'USD', false)}</div>
          ${numeroCuotas > 0 ? `<div class="linea">${numeroCuotas} CUOTAS DE $${formatearPrecio((totalUSD - montoInicial) / numeroCuotas, 'USD', false)}</div>` : ''}
        ` : ''}
        <div class="separador">----------------------------</div>
        <div class="linea">PAGO: ${metodoPago} | ${estado}</div>
        <div class="separador">----------------------------</div>
        <div class="linea bold">${labelGarantia}</div>
        <div class="linea">NO VALIDO SIN TICKET</div>
        <div class="separador">----------------------------</div>
        <div class="linea bold">GRACIAS POR SU COMPRA!</div>
        <div class="linea">REDES: ${empresa.instagram || '@SYSTEMJJ'}</div>
      </div>
      
      <div class="no-print" style="text-align: center; margin-top: 2mm;">
        <button onclick="window.print()" style="padding: 2mm 4mm; background: black; color: white; border: none; cursor: pointer; font-size: 8px;">IMPRIMIR</button>
        <button onclick="window.close()" style="padding: 2mm 4mm; background: #333; color: white; border: none; margin-left: 2mm; cursor: pointer; font-size: 8px;">CERRAR</button>
      </div>
      
      <script>
        window.onload = function() { setTimeout(function() { window.print(); }, 300); };
        window.onafterprint = function() { setTimeout(function() { window.close(); }, 500); };
      </script>
    </body>
    </html>
  `;
  return html;
};

export const imprimirFactura = (venta, cliente, usuario, tasa, productos) => {
  const htmlFactura = generarHTMLFactura(venta, cliente, usuario, tasa, productos);
  const printWindow = window.open('', '_blank', 'width=400,height=550');
  if (!printWindow) { alert('Por favor permite ventanas emergentes para imprimir la factura'); return; }
  printWindow.document.write(htmlFactura);
  printWindow.document.close();
  return printWindow;
};

export const imprimirFacturaSimple = (venta, cliente, usuario, tasa) => {
  const productos = venta.productos || venta.items || [];
  return imprimirFactura(venta, cliente, usuario, tasa, productos);
};

export const imprimirFacturaPOS58 = (venta, cliente, usuario, tasa, products = [], options = {}) => {
  const empresa = obtenerDatosEmpresa(); // 👈 config dinámica

  const duplicateLines = options.duplicateLines !== false;
  const subtotal = parseFloat(venta.subtotalUsd || venta.subtotal_usd || venta.subtotal) || 0;
  const descuento = parseFloat(venta.descuentoUsd || venta.descuentoUSD || venta.descuento || 0);
  const totalUSD = parseFloat(venta.total || venta.totalConInteres || venta.total_usd || 0);
  const tipoVenta = (venta.tipoVenta || venta.tipo_venta || 'CONTADO').toUpperCase();
  const estado = (venta.estado || 'Completada').toUpperCase();
  const metodoPago = (venta.metodoPago || venta.metodo_pago || 'EFECTIVO').toUpperCase();
  const tasaValor = parseFloat(tasa?.precio_usd_bs || tasa?.valor || tasa?.tasaVenta || tasa?.tasa_bs_usd || venta.tasaBsUsd || venta.tasa_bs_usd) || 0;
  const totalBs = totalUSD * tasaValor || parseFloat(venta.totalBs || venta.total_bs) || 0;
  const fecha = formatearFechaTicket(new Date().toISOString());
  const numero = venta.numeroFactura || venta.numero_factura || generarNumeroFactura('ORD');
  const vendedor = usuario ? (usuario.nombre || usuario.nombre_completo || 'SISTEMA').toUpperCase() : 'SISTEMA';
  const clienteNombre = cliente ? `${cliente.nombre || ''} ${cliente.apellido || ''}`.toUpperCase() : 'CONSUMIDOR FINAL';

  let maxGarantiaMeses = 0;
  let maxGarantiaDias = 0;
  if (productos && productos.length > 0) {
    productos.forEach(p => {
      const gMeses = parseInt(p.garantiaMeses || p.garantia_meses || 0);
      const gDias = parseInt(p.garantiaDias || p.garantia_dias || 0);
      if (gMeses > maxGarantiaMeses) maxGarantiaMeses = gMeses;
      if (gDias > maxGarantiaDias) maxGarantiaDias = gDias;
    });
  }
  let labelGarantia = 'GARANTIA 1 MES';
  if (maxGarantiaMeses > 0) {
    labelGarantia = `GARANTIA ${maxGarantiaMeses} ${maxGarantiaMeses === 1 ? 'MES' : 'MESES'}`;
  } else if (maxGarantiaDias > 0) {
    labelGarantia = `GARANTIA ${maxGarantiaDias} ${maxGarantiaDias === 1 ? 'DIA' : 'DIAS'}`;
  }

  let textoTicket = '';
  textoTicket += centro(empresa.direccion) + '\n';
  textoTicket += centro(empresa.ciudad) + '\n';
  textoTicket += centro(`${empresa.telefono1} / ${empresa.whatsapp}`) + '\n';
  textoTicket += linea('-') + '\n';
  textoTicket += centro('ORDEN DE ENTREGA') + '\n';
  textoTicket += linea('-') + '\n';
  textoTicket += centro(`NRO: ${numero} - ${fecha}`) + '\n';
  textoTicket += centro(`${tipoVenta === 'CREDITO' ? 'CREDITO' : 'CONTADO'}`) + '\n';
  textoTicket += linea('-') + '\n';
  textoTicket += centro(`CLIENTE: ${clienteNombre}`) + '\n';
  const clienteDoc = cliente?.documento || '';
  const clienteTel = cliente?.telefono || '';
  if (clienteDoc || clienteTel) {
    textoTicket += centro(`${clienteDoc}${clienteDoc && clienteTel ? ' | ' : ''}${clienteTel}`) + '\n';
  }
  textoTicket += linea('-') + '\n';
  textoTicket += centro('PRODUCTOS') + '\n';

  const productos = Array.isArray(products) ? products : [];
  if (productos.length > 0) {
    productos.forEach(prod => {
      const cantidad = parseFloat(prod.cantidad) || 1;
      const nombre = prod.nombre || prod.nombreProducto || prod.productoNombre || prod.descripcion || 'PRODUCTO';
      const totalProd = parseFloat(prod.subtotalUsd) || parseFloat(prod.precioUnitarioUsd || prod.precio || 0) * cantidad;
      const precioStr = formatearPrecio(prod.precioUnitarioUsd || prod.precio || 0, 'USD', false);
      textoTicket += centro(`${cantidad} ${nombre}`) + '\n';
      textoTicket += centro(`$${precioStr} C/U - TOTAL: $${formatearPrecio(totalProd, 'USD', false)}`) + '\n';
    });
  } else {
    textoTicket += centro('NO HAY PRODUCTOS') + '\n';
  }

  textoTicket += linea('-') + '\n';
  textoTicket += centro(`SUBTOTAL: $${formatearPrecio(subtotal, 'USD', false)}`) + '\n';
  if (descuento > 0) {
    textoTicket += centro(`DESCUENTO: -$${formatearPrecio(descuento, 'USD', false)}`) + '\n';
  }
  textoTicket += linea('-') + '\n';
  textoTicket += centro(`TOTAL: $${formatearPrecio(totalUSD, 'USD', false)}`) + '\n';
  if (tasaValor > 0) {
    textoTicket += centro(`TOTAL Bs: ${formatearPrecio(totalBs, 'BS', true)}`) + '\n';
    textoTicket += centro(`TASA: ${formatearPrecio(tasaValor, 'BS', true)}`) + '\n';
  }
  textoTicket += linea('-') + '\n';
  textoTicket += centro(`PAGO: ${metodoPago} | ${estado}`) + '\n';
  textoTicket += linea('-') + '\n';
  textoTicket += centro(labelGarantia) + '\n';
  textoTicket += centro('NO VALIDO SIN TICKET') + '\n';
  textoTicket += linea('-') + '\n';
  textoTicket += centro('GRACIAS POR SU COMPRA!') + '\n';
  textoTicket += centro(`REDES: ${empresa.instagram || '@SYSTEMJJ'}`) + '\n';

  textoTicket = textoTicket.replace(/\s+$/g, '');
  textoTicket = textoTicket.toUpperCase();

  if (duplicateLines) {
    const lines = textoTicket.split('\n');
    const doubled = [];
    for (let i = 0; i < lines.length; i++) {
      doubled.push(lines[i]);
      doubled.push(lines[i]);
    }
    textoTicket = doubled.join('\n');
  }

  const printWindow = window.open('', '_blank', 'width=300,height=600');
  if (!printWindow) { alert('Por favor permite ventanas emergentes para imprimir la factura'); return; }

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>Factura ${numero}</title>
      <style>
        html,body{margin:0;padding:0;background:#fff;color:#000;}
        body{width:58mm;margin:0 auto;padding:0;}
        pre{display:block;font-family: monospace; font-size: 10px; line-height:1.1; font-weight: 900; white-space: pre; word-break: normal; margin:0 auto; padding:0; width:50mm; text-align:center;}
        @media print { @page { size: 58mm auto; margin: 0mm; } .no-print{display:none !important;} }
      </style>
    </head>
    <body>
      <pre>${textoTicket.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
      <div class="no-print" style="text-align: center; margin-top: 2mm;">
        <button onclick="window.print()" style="padding: 2mm 4mm; background: #000; color: white; border: none; cursor: pointer; font-size: 8px;">IMPRIMIR</button>
        <button onclick="window.close()" style="padding: 2mm 4mm; background: #666; color: white; border: none; cursor: pointer; font-size: 8px;">CERRAR</button>
      </div>
      <script>
        window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };
        window.onafterprint = function(){ setTimeout(function(){ window.close(); }, 500); };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
  return printWindow;
};

export const calcularTotalesVenta = (subtotal, descuento = 0, ivaPorcentaje = 16) => {
  const subtotalNum = parseFloat(subtotal) || 0;
  const descuentoNum = parseFloat(descuento) || 0;
  const subtotalConDescuento = Math.max(0, subtotalNum - descuentoNum);
  const iva = subtotalConDescuento * (ivaPorcentaje / 100);
  const total = subtotalConDescuento + iva;
  return { subtotal: subtotalNum, descuento: descuentoNum, subtotalConDescuento, iva, total, ivaPorcentaje };
};

export default {
  imprimirFactura,
  imprimirFacturaSimple,
  imprimirFacturaPOS58,
  generarNumeroFactura,
  calcularTotalesVenta,
  calcularTotalBolivares,
  formatearNumero,
  formatearPrecio,
  generarHTMLFactura
};